import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { AuthService } from '../../../../services/auth.service';
import { RegistrationDto } from '../../../../model/vens.model';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {LoaderComponent} from "../../../common/loader/loader.component";
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import { FormsModule } from "@angular/forms";
import {BehaviorSubject, Subject} from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: "app-registration-index",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, LoaderComponent, FormsModule],
  templateUrl: "./registration-index.component.html",
  styleUrl: "./registration-index.component.scss",
})
export class RegistrationIndexComponent implements OnInit, OnDestroy {
  protected isLoading: boolean = false;

  // Search fields for Registration parameters
  public registrationIdSearch: string = '';
  public venIdSearch: string = '';
  public vtnIdSearch: string = '';
  public profileNameSearch: string = '';
  public reportOnlySearch: string = '';

  // Debounce search
  private unsubscribe = new Subject<void>();
  private searchParams$ = new BehaviorSubject<any>({
    registrationId: '',
    profileName: '',
    reportOnly: ''
  });

  public isAdmin: boolean = false;
  public displayedRegistrations: RegistrationDto[] = [];
  public error: string | null = null;
  public router: Router;

  protected readonly paginationId = 'registrationsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private registrationService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadRegistrations();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.registrationIdSearch) {
      params['registration_id'] = this.registrationIdSearch.trim();
    }
    if (this.vtnIdSearch) {
      params['vtn_id'] = this.vtnIdSearch.trim();
    }
    if (this.profileNameSearch) {
      params['profile_name'] = this.profileNameSearch.trim();
    }
    if (this.reportOnlySearch) {
      params['reportOnly'] = this.reportOnlySearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();
    this.searchParams$.next({
      registrationId: this.registrationIdSearch,
      venId: this.venIdSearch,
      vtnId: this.vtnIdSearch,
      profileName: this.profileNameSearch,
      reportOnly: this.reportOnlySearch
    });
  }

  loadRegistrations() {
    this.isLoading = true;
    this.error = null;
    const queryParams = this.buildSearchParams();

    this.registrationService.findRegistration(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedRegistrations = response;
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error loading registrations:', err);
          this.error = extractErrorMessage(err, "Failed to load registrations.");
        }
      });
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(registration: any) {
    if (confirm('Are you sure you want to cancel this registration?')) {
      this.registrationService.deleteRegistration(registration.registration_id).subscribe({
        next: () => {
          this.loadRegistrations();
        },
        error: (err) => {
          console.error('Error deleting registration:', err);
          this.error = extractErrorMessage(err, "Failed to cancel registration.");
        }
      });
    }
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadRegistrations();
  }
}
